import { Flex, VStack, Heading, Text, MenuItem } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { listItemVariants } from '@/theme/animations'
import { useAppTranslation } from '@/contexts/LanguageContext'
import HeaderActions from '@/components/FileExplorer/HeaderActions'

const MotionBox = motion.div

interface RootHeaderProps {
  onUpload: () => void
  onUploadFolder: () => void
  onCreateFolder: () => void
  onFilesChange?: () => void
  onFolderTreeChange?: () => void
}

/**
 * The "All Documents" header — what FolderHeader is for a folder, for the top of the tree.
 *
 * Carries the same actions minus the ones that need a folder row to act on (download as ZIP,
 * permissions, rename, delete). The two AI actions run against the root scope, which the API
 * addresses as `folders/root/...` and the modals as a null folder.
 */
export default function RootHeader({
  onUpload,
  onUploadFolder,
  onCreateFolder,
  onFilesChange,
  onFolderTreeChange,
}: RootHeaderProps) {
  const { t } = useAppTranslation(['common', 'files'])

  return (
    <MotionBox variants={listItemVariants}>
      <VStack align="stretch" spacing={2} mb={4}>
        {/* Wraps below `lg`, where the actions claim the full width and drop below the title */}
        <Flex align="center" gap={3} w="full" wrap="wrap">
          <Heading size="md" color="gray.800" fontWeight="bold" flex="1" minW={0}>
            {t('fileExplorer.allDocuments', { ns: 'files' })}
          </Heading>

          <HeaderActions
            folder={null}
            onFilesChange={onFilesChange}
            onFolderTreeChange={onFolderTreeChange}
            menuItems={
              <>
                <MenuItem onClick={onUpload}>
                  {t('fileExplorer.uploadFiles', { ns: 'files' })}
                </MenuItem>
                <MenuItem onClick={onUploadFolder}>
                  {t('fileExplorer.uploadFolder', { ns: 'files' })}
                </MenuItem>
                <MenuItem onClick={onCreateFolder}>
                  {t('fileExplorer.createFolder', { ns: 'files' })}
                </MenuItem>
              </>
            }
          />
        </Flex>

        <Text minW={0} color="gray.600" fontSize="sm" lineHeight="short">
          {t('fileExplorer.browseAllDocuments', { ns: 'files' })}
        </Text>
      </VStack>
    </MotionBox>
  )
}
